import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProductFilter.css';
import { getCultivars, getStyles } from "../actions/ApiCalls";
import WhereToBuy from "./WhereToBuy";
import portfolio from '../static/VINIMARK PORTFOLIO SUMMARY MARCH 2026.pdf';

interface FilterOptionProps {
    item: {
        key: string;
        title: string;
        value: string;
    };
    isActive: boolean;
    onToggle: (value: string) => void;
}

function FilterOption({ item, isActive, onToggle }: FilterOptionProps) {
    return (
        <li key={item.key}>
            <div className={`filter-option ${isActive ? 'is-active' : ''}`} onClick={() => onToggle(item.value)}>
                <div className="filter-radio"></div>
                <span>{item.title}</span>
            </div>
        </li>
    );
}

interface FilterGroupProps {
    filtergroup: {
        slug: string;
        title: string;
        options: Array<{
            key: string;
            title: string;
            value: string;
        }>;
    };
    activeFilter: string | null;
    selectedOptions: string[];
    onOptionToggle: (value: string) => void;
}

function FilterGroup({ filtergroup, activeFilter, selectedOptions, onOptionToggle }: FilterGroupProps) {
    const filterItems = filtergroup.options.map(item => (
        <FilterOption
            key={item.key}
            item={item}
            isActive={selectedOptions.includes(item.value)}
            onToggle={onOptionToggle}
        />
    ));

    const isMultiColumn = filtergroup.slug === 'cultivar' || filtergroup.slug === 'style';

    return (
        <div className={`filter-item ${filtergroup.slug === activeFilter ? 'is-active' : ''}`}>
            <div className="filter-item-title">{filtergroup.title}</div>
            <div className="filter-item-options">
                <ul className={`filter-selector ${isMultiColumn ? 'filter-selector-columns' : ''}`}>
                    {filterItems}
                </ul>
            </div>
        </div>
    );
}

interface ProductFilterProps {
    brands?: any[];
    brand?: string;
    onFilterChange?: (filteredBrands: any[]) => void;
    onSelectedIdsChange?: (selectedIds: number[]) => void;
    onSelectedStyleIdsChange?: (selectedStyleIds: number[]) => void;
    onSelectedCultivarIdsChange?: (selectedCultivarIds: number[]) => void;
    mode?: 'fixed' | 'scroll' | 'hidden';
    additionalFields?: boolean;
}

function ProductFilter({
                           brands = [],
                           brand,
                           onFilterChange,
                           onSelectedIdsChange,
                           onSelectedStyleIdsChange,
                           onSelectedCultivarIdsChange,
                           mode = 'fixed',
                           additionalFields = false,
                       }: ProductFilterProps) {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectedStyleIds, setSelectedStyleIds] = useState<number[]>([]);
    const [selectedCultivarIds, setSelectedCultivarIds] = useState<number[]>([]);
    const [cultivars, setCultivars] = useState<any[]>([]);
    const [styles, setStyles] = useState<any[]>([]);
    const [isMobile, setIsMobile] = useState(false);
    const filterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (mode === 'fixed') {
            document.body.classList.add('fixed-filter-active');
            return () => {
                document.body.classList.remove('fixed-filter-active');
            };
        }
    }, [mode]);

    useEffect(() => {
        fetchCultivars();
        fetchStyles();

        // Check if mobile on mount
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    function fetchCultivars() {
        getCultivars()
            .then(response => setCultivars(response.data))
            .catch(error => console.error("Failed to fetch cultivars:", error));
    }

    function fetchStyles() {
        getStyles()
            .then(response => setStyles(response.data))
            .catch(error => console.error("Failed to fetch styles:", error));
    }

    const cultivarOptions = cultivars.map(cultivar => ({
        key: `cultivar-${cultivar.id}`,
        title: cultivar.name,
        value: `cultivar-${cultivar.id}`
    }));

    const styleOptions = styles.map(style => ({
        key: `style-${style.id}`,
        title: style.name,
        value: `style-${style.id}`
    }));

    const filterGroups = [
        {
            slug: 'style',
            title: 'style',
            options: styleOptions
        },
        {
            slug: 'cultivar',
            title: 'Cultivar',
            options: cultivarOptions
        }
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setActiveFilter(null);
            }
        };

        if (activeFilter !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeFilter]);

    const handleFilterTabClick = (filterSlug: string) => {
        setActiveFilter(activeFilter === filterSlug ? null : filterSlug);
    };

    const handleMobileFilterClick = () => {
        // On mobile, if no filter is active, open 'style' by default
        if (activeFilter === null) {
            setActiveFilter('style');
        } else {
            setActiveFilter(null);
        }
    };

    const handleOptionToggle = (value: string) => {
        setSelectedOptions(prev => {
            const newOptions = prev.includes(value)
                ? prev.filter(option => option !== value)
                : [...prev, value];

            const newStyleIds: number[] = [];
            const newCultivarIds: number[] = [];
            const allIds: number[] = [];

            newOptions.forEach(option => {
                if (option.startsWith('style-')) {
                    const id = Number(option.replace('style-', ''));
                    newStyleIds.push(id);
                    allIds.push(id);
                } else if (option.startsWith('cultivar-')) {
                    const id = Number(option.replace('cultivar-', ''));
                    newCultivarIds.push(id);
                    allIds.push(id);
                }
            });

            setSelectedStyleIds(newStyleIds);
            setSelectedCultivarIds(newCultivarIds);
            setSelectedIds(allIds);

            if (onSelectedIdsChange) onSelectedIdsChange(allIds);
            if (onSelectedStyleIdsChange) onSelectedStyleIdsChange(newStyleIds);
            if (onSelectedCultivarIdsChange) onSelectedCultivarIdsChange(newCultivarIds);

            return newOptions;
        });
    };

    const handleApplyFilter = () => {
        if (selectedOptions.length === 0) return;

        let filterType = '';
        let filters: number[] = [];

        // Get the names of selected styles and cultivars
        const selectedStyleNames = selectedStyleIds.map(id => {
            const style = styles.find(s => s.id === id);
            return style ? style.name : '';
        }).filter(name => name !== '');

        const selectedCultivarNames = selectedCultivarIds.map(id => {
            const cultivar = cultivars.find(c => c.id === id);
            return cultivar ? cultivar.name : '';
        }).filter(name => name !== '');

        if (selectedCultivarIds.length > 0 && selectedStyleIds.length === 0) {
            filterType = 'cultivars';
            filters = selectedCultivarIds;
        } else if (selectedStyleIds.length > 0 && selectedCultivarIds.length === 0) {
            filterType = 'styles';
            filters = selectedStyleIds;
        } else if (selectedCultivarIds.length > 0 && selectedStyleIds.length > 0) {
            filterType = 'cultivars';
            filters = selectedCultivarIds;
        }

        navigate('/products', {
            state: {
                filterType,
                filters,
                brand,                      // Pass brand in navigation state if provided
                selectedStyleNames,         // Pass style names
                selectedCultivarNames,      // Pass cultivar names
            }
        });

        if (onFilterChange) {
            const filteredBrands = brands.filter(brand =>
                selectedOptions.some(option =>
                    brand.name?.toLowerCase().includes(option) ||
                    brand.description?.toLowerCase().includes(option)
                )
            );
            onFilterChange(filteredBrands);
        }

        setActiveFilter(null);
    };

    const [showPanel, setShowPanel] = useState(false);

    const handleOpen = () => setShowPanel(true);
    const handleClose = () => setShowPanel(false);

    const handleClearFilter = () => {
        setSelectedOptions([]);
        setSelectedIds([]);
        setSelectedStyleIds([]);
        setSelectedCultivarIds([]);

        if (onFilterChange) onFilterChange(brands);
        if (onSelectedIdsChange) onSelectedIdsChange([]);
        if (onSelectedStyleIdsChange) onSelectedStyleIdsChange([]);
        if (onSelectedCultivarIdsChange) onSelectedCultivarIdsChange([]);

        setActiveFilter(null);
    };


    if (mode === 'hidden') {
        return null;
    }

    const filterComponents = filterGroups.map(item => (
        <FilterGroup
            key={item.slug}
            filtergroup={item}
            activeFilter={activeFilter}
            selectedOptions={selectedOptions}
            onOptionToggle={handleOptionToggle}
        />
    ));

    const isFilterOpen = activeFilter !== null;
    const filterModeClass = mode === 'fixed' ? 'fixed-filter' : 'scroll-filter';

    return (
        <div ref={filterRef} className={`filter-panel ${isFilterOpen ? 'is-open' : ''} ${filterModeClass}`}>
            <div className={`filter-content ${isFilterOpen ? 'is-visible' : ''}`}>
                {/* Mobile filter tabs at top when opened - ALWAYS show when filter is open on mobile */}
                {isMobile && isFilterOpen && (
                    <div
                        style={{
                            padding: '15px 24px',
                            borderBottom: '1px solid #eee',
                            background: 'white',
                            marginBottom: '16px'
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                                display: 'flex',
                                flexDirection: 'row',
                                fontSize: '10px',
                                fontWeight: 400,
                                textTransform: 'uppercase',
                                marginLeft: '-6px'
                            }}>
                                <li style={{
                                    padding: '2px 4px',
                                    cursor: 'default',
                                    fontWeight: 'bold'
                                }}>
                                    Filter by:
                                </li>
                                <li
                                    style={{
                                        padding: '2px 4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        textDecoration: activeFilter === 'style' ? 'underline' : 'none',
                                        textUnderlineOffset: '2px'
                                    }}
                                    onClick={() => setActiveFilter('style')}
                                >
                                    Style
                                </li>
                                <li
                                    style={{
                                        padding: '2px 4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        textDecoration: activeFilter === 'cultivar' ? 'underline' : 'none',
                                        textUnderlineOffset: '2px',
                                        color: 'black',
                                        backgroundColor: 'white'
                                    }}
                                    onClick={() => setActiveFilter('cultivar')}
                                >
                                    Cultivar
                                </li>
                            </ul>
                        </div>
                    </div>
                )}

                <div className="filter-items">
                    {filterComponents}
                </div>

                <div
                    className="filter-button"
                    style={isMobile ? {
                        flexDirection: 'column',
                        gap: '8px'
                    } : {}}
                >
                    <button
                        className="button button-inverted button-filter-apply"
                        onClick={handleApplyFilter}
                        disabled={selectedOptions.length === 0}
                        style={isMobile ? {
                            width: '100%',
                            flex: 'none'
                        } : {}}
                    >
                        Apply
                    </button>
                    <button
                        className="button button-filter-clear"
                        onClick={handleClearFilter}
                        style={isMobile ? {
                            width: '100%',
                            flex: 'none'
                        } : {}}
                    >
                        Clear all
                    </button>
                </div>
            </div>

            <div className="filter-tabs" style={{display: 'flex'}}>
                <div style={{flex: isMobile ? 2 : 1}}>
                    <ul>
                        {isMobile ? (
                            <li
                                className="filter-tabs-title"
                                onClick={handleMobileFilterClick}
                                style={{cursor: 'pointer'}}
                            >
                                Filter
                            </li>
                        ) : (
                            // Desktop view: Show "Filter by: Style Cultivar"
                            <>
                                <li className="filter-tabs-title">Filter by:</li>
                                <li
                                    className={activeFilter === 'style' ? 'is-active' : ''}
                                    onClick={() => handleFilterTabClick('style')}
                                >
                                    Style
                                </li>
                                <li
                                    className={activeFilter === 'cultivar' ? 'is-active' : ''}
                                    onClick={() => handleFilterTabClick('cultivar')}
                                >
                                    Cultivar
                                </li>
                            </>
                        )}
                    </ul>
                </div>
                {additionalFields && (
                    <div style={{flex: isMobile ? 8 : 1, marginLeft: isMobile ? '16px' : '50px'}}>
                        <ul>
                            <li
                                className="filter-tabs-title"
                                style={{ cursor: 'pointer'}}
                                onClick={handleOpen}
                            >
                                Where to buy
                            </li>
                            <li className="filter-tabs-title" style={{ cursor: 'pointer'}}>
                                <a href={portfolio} target="_blank" rel="noopener noreferrer" style={{textDecoration: "none"}}>
                                    Full portfolio
                                </a>

                            </li>
                            <li
                                className="filter-tabs-title"
                                onClick={() => {
                                    window.open('https://vinimark.mobi/logon.aspx?ReturnUrl=%2f', '_blank');
                                }}
                                style={{marginLeft: '10px', cursor: 'pointer'}}
                            >
                                order online
                            </li>
                        </ul>
                    </div>
                )}
            </div>
            <WhereToBuy isOpen={showPanel} onClose={handleClose} brand={brand} />
        </div>
    );
}

export default ProductFilter;
